import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portfolioData, setPortfolioData] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle' | 'syncing' | 'synced' | 'error'
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Ref for debounce timer
  const syncTimeoutRef = useRef(null);
  const AUTO_SYNC_DELAY = 2000; // 2 seconds debounce

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      // Load portfolio data after auth check
      await loadPortfolioData();
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Load portfolio data from server
  const loadPortfolioData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/portfolio-data`, {
        withCredentials: true
      });
      setPortfolioData(response.data);
      
      // Sync to localStorage for offline access
      if (response.data.imported_positions) {
        localStorage.setItem('importedPositions', JSON.stringify(response.data.imported_positions));
      }
      if (response.data.hidden_positions) {
        localStorage.setItem('hiddenTastytradePositions', JSON.stringify(response.data.hidden_positions));
      }
      if (response.data.position_overrides) {
        localStorage.setItem('tastytradePositionOverrides', JSON.stringify(response.data.position_overrides));
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to load portfolio data:', error);
      return null;
    }
  };

  // Sync portfolio data to server
  const syncPortfolioData = useCallback(async () => {
    if (!user) return false;
    
    setSyncStatus('syncing');
    
    try {
      const data = {
        imported_positions: JSON.parse(localStorage.getItem('importedPositions') || '[]'),
        hidden_positions: JSON.parse(localStorage.getItem('hiddenTastytradePositions') || '[]'),
        position_overrides: JSON.parse(localStorage.getItem('tastytradePositionOverrides') || '{}')
      };
      
      await axios.post(`${BACKEND_URL}/api/auth/portfolio-data`, data, {
        withCredentials: true
      });
      
      setPortfolioData(data);
      setSyncStatus('synced');
      setLastSyncTime(new Date());
      
      // Reset status to idle after 3 seconds
      setTimeout(() => setSyncStatus('idle'), 3000);
      
      return true;
    } catch (error) {
      console.error('Failed to sync portfolio data:', error);
      setSyncStatus('error');
      
      // Reset status to idle after 5 seconds
      setTimeout(() => setSyncStatus('idle'), 5000);
      
      return false;
    }
  }, [user]);

  // Auto-sync with debounce - call this when portfolio data changes
  const triggerAutoSync = useCallback(() => {
    if (!user) return;
    
    // Clear any existing timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }
    
    // Set pending status
    setSyncStatus('syncing');
    
    // Debounce the actual sync
    syncTimeoutRef.current = setTimeout(() => {
      syncPortfolioData();
    }, AUTO_SYNC_DELAY);
  }, [user, syncPortfolioData]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Google Sign-In
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Fresh Google sign-in required (server-enforced, 5-minute window) before account deletion
  const reauthWithGoogleForDeletion = () => {
    sessionStorage.setItem('ow_delete_reauth', '1');
    loginWithGoogle();
  };

  // Process Google OAuth callback
  const processGoogleCallback = async (sessionId) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/google/session`, {
        session_id: sessionId
      }, {
        withCredentials: true
      });
      
      setUser(response.data.user);
      await loadPortfolioData();
      return response.data.user;
    } catch (error) {
      console.error('Google auth error:', error);
      throw error;
    }
  };

  // Email/Password Signup
  const signup = async (email, password, name) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/signup`, {
        email,
        password,
        name
      }, {
        withCredentials: true
      });
      
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      throw error.response?.data?.detail || 'Signup failed';
    }
  };

  // Email/Password Login
  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        email,
        password,
        remember_me: rememberMe
      }, {
        withCredentials: true
      });
      
      setUser(response.data.user);
      await loadPortfolioData();
      return response.data.user;
    } catch (error) {
      throw error.response?.data?.detail || 'Login failed';
    }
  };

  // Guest / comp full-access backdoor (server-side shared secret)
  const guestAccess = async (accessCode) => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/guest-access`, {
        access_code: accessCode
      }, { withCredentials: true });
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      throw error.response?.data?.detail || 'Invalid access code';
    }
  };

  // Logout
  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, {
        withCredentials: true
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setPortfolioData(null);
    }
  };

  // Self-service account deletion
  const deleteAccount = async (confirmation, password) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/auth/account`, {
        data: { confirmation, password },
        withCredentials: true
      });
      setUser(null);
      setPortfolioData(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.code) throw detail;
      throw detail || 'Account deletion failed';
    }
  };

  const value = {
    user,
    loading,
    portfolioData,
    isAuthenticated: !!user,
    syncStatus,
    lastSyncTime,
    loginWithGoogle,
    reauthWithGoogleForDeletion,
    processGoogleCallback,
    signup,
    login,
    logout,
    deleteAccount,
    guestAccess,
    loadPortfolioData,
    syncPortfolioData,
    triggerAutoSync, // New: auto-sync with debounce
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
